import { Moment } from 'moment';
import groupBy from 'lodash/groupBy';
import upperFirst from 'lodash/upperFirst';

import { getI18nLocale, Locale, translate } from '@front/i18n';

import moment from './moment';
import { formatTime, parseTime } from './time';
import { formatStringListWithSeparator } from './formatting';

const t = translate('date');

export type DateFormat =
  | 'date_default'
  | 'date_iso_8601'
  | 'date_default_short'
  | 'date_day_short'
  | 'date_day_month'
  | 'date_day_month_short'
  | 'date_day_month_year'
  | 'date_day_month_year_short'
  | 'date_month'
  | 'date_month_short'
  | 'date_month_year'
  | 'date_month_year_short'
  | 'date_month_year_shorter'
  | 'date_weekday'
  | 'date_weekday_short'
  | 'date_weekday_plural'
  | 'date_weekday_plural_at'
  | 'date_weekday_day_month'
  | 'date_weekday_day_month_short'
  | 'date_weekday_day_month_short_at'
  | 'date_weekday_day_month_year'
  | 'date_weekday_day_month_year_short'
  | 'date_year';

export type DateTimeFormat =
  | 'datetime_weekday_at_time'
  | 'datetime_weekday_day_month_year_at_time'
  | 'datetime_weekday_day_month_at_time'
  | 'datetime_weekday_day_month_at_time_short'
  | 'datetime_weekday_long_day_long_month_at_time'
  | 'datetime_date_standard_dash_time'
  | 'datetime_day_month_at_time'
  | 'datetime_short_date_at_time'
  | 'datetime_date_standard_at_time'
  | 'datetime_timestamp'
  | 'datetime_api_timestamp';

export type LocaleFormats = Record<DateFormat | DateTimeFormat, string>;

const DATE: Record<string, DateFormat> = {
  DEFAULT: 'date_default',
  ISO_8601: 'date_iso_8601',
  DEFAULT_SHORT: 'date_default_short',
  DAY_SHORT: 'date_day_short',
  DAY_MONTH: 'date_day_month',
  DAY_MONTH_SHORT: 'date_day_month_short',
  DAY_MONTH_YEAR: 'date_day_month_year',
  DAY_MONTH_YEAR_SHORT: 'date_day_month_year_short',
  MONTH: 'date_month',
  MONTH_YEAR: 'date_month_year',
  MONTH_YEAR_SHORT: 'date_month_year_short',
  MONTH_YEAR_SHORTER: 'date_month_year_shorter',
  WEEKDAY: 'date_weekday',
  WEEKDAY_SHORT: 'date_weekday_short',
  WEEKDAY_PLURAL: 'date_weekday_plural',
  WEEKDAY_PLURAL_AT: 'date_weekday_plural_at',
  WEEKDAY_DAY_MONTH: 'date_weekday_day_month',
  WEEKDAY_DAY_MONTH_SHORT: 'date_weekday_day_month_short',
  WEEKDAY_DAY_MONTH_SHORT_AT: 'date_weekday_day_month_short_at',
  WEEKDAY_DAY_MONTH_YEAR: 'date_weekday_day_month_year',
  WEEKDAY_DAY_MONTH_YEAR_SHORT: 'date_weekday_day_month_year_short',
  YEAR: 'date_year',
};

const DATETIME: Record<string, DateTimeFormat> = {
  DAY_MONTH_AT_TIME: 'datetime_day_month_at_time',
  WEEKDAY_AT_TIME: 'datetime_weekday_at_time',
  WEEKDAY_DAY_MONTH_YEAR_AT_TIME: 'datetime_weekday_day_month_year_at_time',
  WEEKDAY_DAY_MONTH_AT_TIME: 'datetime_weekday_day_month_at_time',
  WEEKDAY_DAY_MONTH_AT_TIME_SHORT: 'datetime_weekday_day_month_at_time_short',
  WEEKDAY_LONG_DAY_LONG_MONTH_AT_TIME: 'datetime_weekday_long_day_long_month_at_time',
  DATE_STANDARD_DASH_TIME: 'datetime_date_standard_dash_time',
  SHORT_DATE_AT_TIME: 'datetime_short_date_at_time',
  DATE_STANDARD_AT_TIME: 'datetime_date_standard_at_time',
  TIMESTAMPS: 'datetime_timestamp',
  API_TIMESTAMP: 'datetime_api_timestamp',
};

export const FORMATS = {
  DATE,
  DATETIME,
};

const STANDARD_DATE = {
  format: 'YYYY-MM-DD',
  regex: /^\d{4}-\d{2}-\d{2}$/,
};

const COMMON_FORMATS = {
  date_default: 'DD/MM/YYYY', // 31/01/2020
  date_iso_8601: 'YYYY-MM-DD', // 2020-01-31
  date_default_short: 'DD/MM/YY', // 31/01/20
  date_day_short: 'DD', // 01
  date_day_month: 'D MMMM', // 1 January
  date_day_month_short: 'D MMM', // 1 Jan
  date_day_month_year: 'D MMMM YYYY', // 1 January 2020
  date_day_month_year_short: 'D MMM YYYY', // 1 Jan 2020
  date_month: 'MMMM', // January
  date_month_short: 'MMM', // January
  date_month_year: 'MMMM YYYY', // January 2020
  date_month_year_short: 'MM/YY', // 01/20
  date_month_year_shorter: 'MMM YYYY', // Jan 2020
  date_weekday: 'dddd', // Monday
  date_weekday_short: 'ddd', // Mon
  date_weekday_plural: 'dddd\\s', // Mondays
  date_weekday_day_month: 'dddd D MMMM', // Monday 1 January
  date_weekday_day_month_short: 'ddd D MMM', // Mon 1 Jan
  date_weekday_day_month_year: 'dddd D MMMM YYYY', // Monday 1 January 2020
  date_weekday_day_month_year_short: 'ddd D MMM YYYY', // Mon 1 Jan 2020
  date_year: 'YYYY', // 2020
};

const DATE_FORMATTING: Record<Locale, LocaleFormats> = {
  fr: {
    ...COMMON_FORMATS,
    date_weekday_plural_at: '\\L\\e\\s dddd\\s \\à', // Les Lundis à
    date_weekday_day_month_short_at: 'ddd D MMM \\à', // lun. 1 janv. à

    datetime_weekday_at_time: 'dddd \\à HH[h]mm', // lundi à 12h00
    datetime_weekday_day_month_year_at_time: 'dddd D MMMM YYYY \\à HH[h]mm', // lundi 1 janvier 2020 à 12h00
    datetime_weekday_day_month_at_time: 'ddd D MMMM \\à HH[h]mm', // lun. 1 janvier à 12h00
    datetime_weekday_day_month_at_time_short: 'ddd D MMM \\à HH[h]mm', // lun. 1 janv. à 12h00
    datetime_weekday_long_day_long_month_at_time: 'dddd D MMMM \\à HH[h]mm', // lundi 1 janvier à 12h00
    datetime_date_standard_dash_time: 'DD/MM/YYYY - HH[h]mm', // 31/01/2020 - 12h00
    datetime_day_month_at_time: 'D MMMM \\à HH[h]mm', // 1 janvier à 12h00
    datetime_short_date_at_time: 'DD/MM \\à HH[h]mm', // 01/01 à 12h00
    datetime_date_standard_at_time: 'DD/MM/YYYY \\à HH[h]mm', // 01/01/2020 à 12h00
    datetime_timestamp: 'YYYY-MM-DDTHH:mm:ss', // 2020-10-01T12:15:12
    datetime_api_timestamp: 'YYYY-MM-DD HH:mm:ss', // 2020-10-01 12:15:12
  },
  en: {
    ...COMMON_FORMATS,
    date_weekday_plural_at: 'dddd\\s \\a\\t', // Mondays at
    date_weekday_day_month_short_at: 'ddd D MMM \\a\\t', // Mon 1 Jan at

    datetime_weekday_at_time: 'dddd \\a\\t HH[:]mm', // Monday at 12:00
    datetime_weekday_day_month_year_at_time: 'dddd D MMMM YYYY \\a\\t HH[:]mm', // Monday 1 January 2020 at 12:00
    datetime_weekday_day_month_at_time: 'ddd D MMMM \\a\\t HH[:]mm', // Mon 1 January at 12:00
    datetime_weekday_day_month_at_time_short: 'ddd D MMM \\a\\t HH[:]mm', // Mon 1 Jan at 12:00
    datetime_weekday_long_day_long_month_at_time: 'dddd D MMMM \\a\\t HH[:]mm', // Monday 1 January at 12:00
    datetime_date_standard_dash_time: 'DD/MM/YYYY - HH[:]mm', // 31/01/2020 - 12:00
    datetime_day_month_at_time: 'D MMMM \\a\\t HH[:]mm', // 1 January at 12:00
    datetime_short_date_at_time: 'DD/MM \\a\\t HH[:]mm', // 01/01 at 12:00
    datetime_date_standard_at_time: 'DD/MM/YYYY \\a\\t HH[:]mm', // 01/01/2020 at 12:00
    datetime_timestamp: 'YYYY-MM-DDTHH:mm:ss', // 2020-10-01T12:15:12
    datetime_api_timestamp: 'YYYY-MM-DD HH:mm:ss', // 2020-10-01 12:15:12
  },
};
const getLocaleDateFormatting = () => DATE_FORMATTING[getI18nLocale()];

const getLocaleDefaultFormat = () => {
  const formats = getLocaleDateFormatting();
  return formats[FORMATS.DATE.DEFAULT];
};

/**
 * This function is used to check the format of the value returned by the date inputs we use in web.
 * At the time this comment is written, we cannot remember the exact reason why we put this logic in
 * place, but we suspect it has something to do with the OS (we believe it is because inputs of type
 * "date" in Android and iOS do not return the same format of value). At least, we know currently we
 * only receive these two formats : either 2020-01-31 or 31/01/2020.
 *
 * TODO : find out the exact reason.
 */
export const getDateFormat = (value: string) =>
  STANDARD_DATE.regex.test(value) ? STANDARD_DATE.format : getLocaleDefaultFormat();

export const formatDate = (
  value: Moment | string | undefined,
  format: DateFormat | DateTimeFormat = 'date_default',
) => {
  const formats = getLocaleDateFormatting();
  return (moment.isMoment(value) ? value : moment(value)).format(formats[format]);
};

export const parseDate = (value: string) => moment(value, getDateFormat(value), true);

export const parseValidDate = (value: string) => {
  const date = parseDate(value);
  return date.isValid() ? date : undefined;
};

export const formatStandardDate = (date: Moment | string) =>
  (moment.isMoment(date) ? date : moment(date)).format(STANDARD_DATE.format);

export const setTime = (date: Moment, timeValue?: string) => {
  const time = parseTime(timeValue);
  return date.clone().set({
    hour: time.hour(),
    minute: time.minute(),
    second: 0,
    millisecond: 0,
  });
};

export const getDatePlaceholder = () => t('.input.placeholder');

const getDurationDifference = (timestamp: Moment) => {
  const now = moment();

  const start = timestamp.isBefore(now) ? timestamp.clone() : now;
  const end = timestamp.isBefore(now) ? now : timestamp.clone();

  const years = end.diff(start, 'years');
  start.add(years, 'years');

  const months = end.diff(start, 'months');
  start.add(months, 'months');

  const days = end.diff(start, 'days');
  start.add(days, 'days');

  return { years, months, days };
};

export const formatDurationDifference = (timestamp?: string | Moment) => {
  const { years, months, days } = getDurationDifference(moment(timestamp));

  const formattedYears = t('.elapsed_duration.x_years', { count: years });
  const formattedMonths = t('.elapsed_duration.x_months', { count: months });
  const formattedDays = t('.elapsed_duration.x_days', { count: days });

  const timeArray = [
    ...(years > 0 ? [formattedYears] : []),
    ...(months > 0 ? [formattedMonths] : []),
    ...(days > 0 ? [formattedDays] : []),
  ];

  return formatStringListWithSeparator(timeArray) || formattedDays;
};

const getElapsedDuration = (timestamp: Moment, now: Moment) => {
  const diffInMinutes = Math.abs(now.diff(timestamp, 'minutes'));
  const diffInHours = Math.abs(now.diff(timestamp, 'hours'));
  const diffInDays = Math.abs(now.diff(timestamp, 'days'));

  if (diffInMinutes < 15) return t('.elapsed_duration.less_than_minute', { count: 15 });
  if (diffInMinutes < 30) return t('.elapsed_duration.x_minutes', { count: 15 });
  if (diffInMinutes < 45) return t('.elapsed_duration.x_minutes', { count: 30 });
  if (diffInMinutes < 60) return t('.elapsed_duration.x_minutes', { count: 45 });
  if (diffInHours < 24) return t('.elapsed_duration.x_hours', { count: Math.floor(diffInHours) });
  if (diffInDays < 31) return t('.elapsed_duration.x_days', { count: Math.floor(diffInDays) });
  return null;
};

export const formatElapsedDuration = (value: string | Moment) => {
  const now = moment();
  const timestamp = moment(value);
  const direction = timestamp.isBefore(now) ? 'past' : 'future';
  const time = getElapsedDuration(timestamp, now);

  if (time) {
    return t(`.elapsed_duration.${direction}`, { time });
  }
  return t('.elapsed_duration.date', { date: formatDate(timestamp, FORMATS.DATE.DAY_MONTH_YEAR) });
};

export const formatTimesByDate = (
  dates: (Moment | string)[],
  { separator = ', ', dayWithUpperFirst = true } = {},
) => {
  const datesGroupedByDate = groupBy(dates, formatStandardDate);

  return Object.keys(datesGroupedByDate).map((date) => {
    const [first, ...rest] = datesGroupedByDate[date];
    const formattedDate = formatDate(first, FORMATS.DATETIME.WEEKDAY_DAY_MONTH_AT_TIME_SHORT);
    const formattedDates = [
      dayWithUpperFirst ? upperFirst(formattedDate) : formattedDate,
      ...rest.map((d) => formatTime(d)),
    ];

    return formattedDates.join(separator);
  });
};

export const getStandardDateFormat = () => COMMON_FORMATS.date_default;

/*
 * To avoid timezone bugs when the user country cannot be defined (not logged in, no country in AsyncStorage),
 * we send datetimes with no timezone to the API.
 * The backend handle dates with no timezone very well, by applying the user account country timezone automatically.
 * See https://wecasa.atlassian.net/browse/BUG-845 and https://github.com/wecasa/front/pull/3759 for details.
 */
export const formateDateForAPI = (datetime: Moment) => formatDate(datetime, FORMATS.DATETIME.API_TIMESTAMP);
